import {
  DecisionAction,
  DecisionActionCompact,
  DecisionConfidence,
  DecisionPriority,
  DecisionRisk,
  DecisionSummary,
  DetectedDatesSummary,
  DocumentPulse,
  HomeMetric,
  SystemActivity
} from "../../../models/home";

export const BASE_TIME = Date.now();

export function isoFromNow(hours: number): string {
  return new Date(BASE_TIME + hours * 60 * 60 * 1000).toISOString();
}

export function buildPriority(score: number, tier: string, finalReason: string): DecisionPriority {
  return {
    score,
    tier,
    finalReason,
    base: Math.max(score - 12, 0),
    urgency: 8,
    dateProximity: 18,
    riskBoost: 12,
    artifactBoost: 6,
    userStateAdjustment: 4
  };
}

export function buildRisk(level: string, reason: string): DecisionRisk {
  return {
    level,
    category: "operational",
    reason,
    impactAreas: ["billing", "renewal"],
    escalationTriggers: ["due_date"],
    evidenceIds: ["evidence-1"]
  };
}

export function buildConfidence(overall: number, warnings: readonly string[] = []): DecisionConfidence {
  const level = overall >= 0.85 ? "high" : overall >= 0.7 ? "medium" : "low";
  return {
    overall,
    level,
    classification: overall,
    dateExtraction: Math.max(overall - 0.04, 0),
    actionSelection: Math.max(overall - 0.06, 0),
    explanation: Math.max(overall - 0.03, 0),
    warnings
  };
}

export function buildCompactAction(
  actionKey: string,
  label: string,
  enabled: boolean,
  options: Partial<DecisionActionCompact> = {}
): DecisionActionCompact {
  return {
    actionKey,
    label,
    source: options.source ?? "policy-engine",
    requiresUserAction: options.requiresUserAction ?? true,
    deadline: options.deadline ?? isoFromNow(24),
    enabled,
    disabledReason: options.disabledReason,
    kind: options.kind,
    placement: options.placement,
    requiresInput: options.requiresInput ?? false,
    confirmationRequired: options.confirmationRequired ?? false,
    expectedState: options.expectedState,
    refreshHints: options.refreshHints ?? [],
    riskLevel: options.riskLevel,
    executionMode: options.executionMode
  };
}

export function buildAction(
  actionKey: string,
  label: string,
  enabled: boolean,
  options?: Partial<DecisionAction>
): DecisionAction {
  return {
    ...buildCompactAction(actionKey, label, enabled, options),
    description: options?.description ?? "Run the recommended business action with the current evidence set.",
    sourceConfidence: options?.sourceConfidence ?? 0.92,
    completionCriteria: options?.completionCriteria ?? ["Validate evidence", "Confirm destination system"],
    payloadSchema: options?.payloadSchema,
    inputSchema: options?.inputSchema ?? null
  };
}

export function buildSummary(): DecisionSummary {
  return {
    total: 8,
    actNow: 2,
    actSoon: 2,
    actWithoutRush: 1,
    waiting: 3,
    blocked: 0,
    safeToIgnore: 1,
    completed: 0,
    averagePriority: 78,
    criticalRisk: 2,
    missingInformationCount: 3,
    lowConfidenceCount: 1,
    nextRefreshAt: isoFromNow(2)
  };
}

export function buildPulse(): DocumentPulse {
  return {
    systemState: "attention",
    systemMessage: "Two decisions need a human confirmation before execution.",
    upcoming: 5,
    overdue: 2,
    documentsUnderstood: {
      totalDocuments: 418,
      artifactTypesDetected: 31,
      categoriesDetected: 74
    },
    artifactTypes: [
      { type: "Invoices", count: 142 },
      { type: "Supplier contracts", count: 91 },
      { type: "Compliance reports", count: 63 }
    ],
    recentClassifications: [
      { artifactType: "Invoice", category: "Finance", subcategory: "Accounts payable" },
      { artifactType: "Contract", category: "Legal", subcategory: "Renewals" },
      { artifactType: "Report", category: "Compliance", subcategory: "Audit" }
    ]
  };
}

export function buildDatesSummary(): DetectedDatesSummary {
  return {
    totalDetected: 6,
    byRole: {
      renewal: 2,
      compliance: 2,
      billing: 1,
      vendor: 1
    },
    roleLabels: {
      renewal: "Renewal",
      compliance: "Compliance",
      billing: "Billing",
      vendor: "Vendor"
    },
    monitoredDates: [
      {
        id: "date-1",
        documentName: "Supplier master agreement",
        role: "renewal",
        state: "urgent",
        dueDate: isoFromNow(12),
        owner: "Legal Ops",
        tooltip: "Detected in clause 12 with mandatory notice period.",
        triggerReason: "Renewal notice due",
        queueItemId: "queue-1"
      },
      {
        id: "date-2",
        documentName: "Invoice batch May",
        role: "billing",
        state: "active",
        dueDate: isoFromNow(48),
        owner: "Accounts Payable",
        tooltip: "The due date was extracted from the supplier remittance.",
        triggerReason: "Payment deadline",
        queueItemId: "queue-2"
      },
      {
        id: "date-3",
        documentName: "Audit evidence package",
        role: "compliance",
        state: "monitoring",
        dueDate: isoFromNow(72),
        owner: "Compliance",
        tooltip: "The filing window starts this week.",
        triggerReason: "Regulatory filing",
        queueItemId: "queue-3"
      },
      {
        id: "date-4",
        documentName: "Vendor onboarding exception",
        role: "vendor",
        state: "active",
        dueDate: isoFromNow(96),
        owner: "Procurement",
        tooltip: "Temporary approval expires after the onboarding grace period.",
        triggerReason: "Temporary approval",
        queueItemId: "queue-4"
      }
    ]
  };
}

export function buildActivity(): readonly SystemActivity[] {
  return [
    {
      type: "classification",
      description: "The policy engine reclassified a supplier invoice as a priority billing action.",
      timestamp: isoFromNow(-2),
      documentsAffected: 3,
      documentsExamples: ["INV-2026-041", "INV-2026-042"],
      impact: {
        kind: "priority_shift",
        summary: "One decision moved from act soon to act now.",
        affectedDocuments: ["doc-1"],
        impactedQueueItems: ["queue-2"],
        relatedRole: "billing"
      }
    },
    {
      type: "validation",
      description: "A user confirmed the customer billing code and unblocked the action.",
      timestamp: isoFromNow(-8),
      documentsAffected: 1,
      documentsExamples: ["Customer invoice package"],
      impact: {
        kind: "reclassification",
        summary: "The next best decision is now executable.",
        affectedDocuments: ["doc-2"],
        impactedQueueItems: ["queue-5"],
        relatedRole: "billing"
      }
    },
    {
      type: "ingestion",
      description: "New compliance evidence was imported from the shared drive.",
      timestamp: isoFromNow(-26),
      documentsAffected: 5,
      documentsExamples: ["Audit evidence package"],
      impact: {
        kind: "new_date",
        summary: "Two monitored dates were added to the support panel.",
        affectedDocuments: ["doc-3", "doc-4"],
        impactedQueueItems: ["queue-3", "queue-4"],
        relatedRole: "compliance"
      }
    }
  ];
}

export function buildMetrics(): readonly HomeMetric[] {
  return [
    {
      label: "pending_documents",
      value: "24",
      helperText: "Documents waiting for the next evaluation window.",
      trend: "down",
      trendLabel: "-3 vs yesterday"
    },
    {
      label: "evaluated_documents",
      value: "394",
      helperText: "Items evaluated during the active monitoring window.",
      trend: "up",
      trendLabel: "+18 today"
    },
    {
      label: "error_rate",
      value: "0.4%",
      helperText: "Recoverable Home IA pipeline failures in the last 24h.",
      trend: "steady",
      trendLabel: "Stable"
    },
    {
      label: "throughput_eval_per_hour",
      value: "62",
      helperText: "Average evaluation throughput for the current tenant.",
      trend: "up",
      trendLabel: "+7 per hour"
    }
  ];
}

